/** @format */

import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
	Avatar,
	AvatarGroup,
	Box,
	Button,
	IconButton,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import { SxProps, Theme, keyframes } from "@mui/system";
import MarkEmailUnreadRoundedIcon from "@mui/icons-material/MarkEmailUnreadRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { AppDispatch } from "../../redux/store/store";
import { fetchCircleInvitations } from "../../redux/actions/circleInvitationAction";
import {
	selectAwaitingVerificationInvitations,
	selectPendingCircleInvitations,
} from "../../redux/selectors/circleInvitationSelector";
import { selectCurrentUser } from "../../redux/selectors/userSelector";
import { buttonColors } from "../../utility/types/constant";

const DISMISSED_STORAGE_KEY = "dismissedCircleInviteIds";
const DAY_MS = 24 * 60 * 60 * 1000;
const FONT = "'Montserrat', sans-serif";

const slideIn = keyframes`
	0% { opacity: 0; transform: translateY(-14px) scale(0.98); }
	100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const envelopeWiggle = keyframes`
	0%, 70%, 100% { transform: rotate(0deg); }
	75% { transform: rotate(-12deg); }
	80% { transform: rotate(10deg); }
	85% { transform: rotate(-6deg); }
	90% { transform: rotate(4deg); }
`;

const pulseRing = keyframes`
	0% { box-shadow: 0 0 0 0 rgba(30, 193, 149, 0.55); }
	70% { box-shadow: 0 0 0 14px rgba(30, 193, 149, 0); }
	100% { box-shadow: 0 0 0 0 rgba(30, 193, 149, 0); }
`;

const shimmer = keyframes`
	0% { transform: translateX(-120%) skewX(-20deg); }
	60%, 100% { transform: translateX(260%) skewX(-20deg); }
`;

const readDismissedIds = (): string[] => {
	try {
		const raw = sessionStorage.getItem(DISMISSED_STORAGE_KEY);
		const parsed = raw ? JSON.parse(raw) : [];
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
};

const formatExpiry = (expiresAt?: string) => {
	if (!expiresAt) return "";
	const msLeft = new Date(expiresAt).getTime() - Date.now();
	if (msLeft <= DAY_MS) return "Expires today";
	const days = Math.ceil(msLeft / DAY_MS);
	return `Expires in ${days} days`;
};

interface PendingInvitesCtaProps {
	sx?: SxProps<Theme>;
}

/**
 * Dashboard call-to-action shown whenever the user has circle invitations
 * waiting for a response, or (once verified) invitations they accepted before
 * verifying and can now finish joining. Clicking it opens /view-invitation where they can
 * accept or deny. Dismissing hides it for the current browser session only;
 * a newly received invitation brings it back.
 */
const PendingInvitesCta = ({ sx }: PendingInvitesCtaProps) => {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const pendingInvitations = useSelector(selectPendingCircleInvitations);
	const awaitingVerificationInvitations = useSelector(
		selectAwaitingVerificationInvitations,
	);
	const currentUser = useSelector(selectCurrentUser);
	const isUserVerified = currentUser?.isVerified === true;

	// The invitations slice is persisted, so wait for a fresh fetch before
	// rendering to avoid flashing stale (or another account's) invitations.
	const [hasFetched, setHasFetched] = useState(false);
	const [dismissedIds, setDismissedIds] = useState<string[]>(readDismissedIds);

	useEffect(() => {
		let isMounted = true;
		dispatch(fetchCircleInvitations()).then((result) => {
			// On failure the slice still holds persisted data, so stay hidden.
			if (isMounted) {
				setHasFetched(fetchCircleInvitations.fulfilled.match(result));
			}
		});
		return () => {
			isMounted = false;
		};
	}, [dispatch]);

	// Verified users with invites they accepted before verifying can finish joining.
	const finishJoiningInvitations = useMemo(
		() =>
			isUserVerified ?
				awaitingVerificationInvitations.filter(
					(inv) => !dismissedIds.includes(inv._id),
				)
			:	[],
		[isUserVerified, awaitingVerificationInvitations, dismissedIds],
	);

	const newInvitations = useMemo(
		() => pendingInvitations.filter((inv) => !dismissedIds.includes(inv._id)),
		[pendingInvitations, dismissedIds],
	);

	const visibleInvitations = useMemo(
		() => [...finishJoiningInvitations, ...newInvitations],
		[finishJoiningInvitations, newInvitations],
	);

	if (!hasFetched || visibleInvitations.length === 0) {
		return null;
	}

	const count = visibleInvitations.length;
	const latest = visibleInvitations[0];
	const latestCircleName = latest.circle?.circleName || "a circle";
	const isOnlyFinishJoining = newInvitations.length === 0;

	let eyebrow: string;
	let title: string;
	let subtitle: string;
	let ctaLabel: string;

	if (isOnlyFinishJoining && count === 1) {
		eyebrow = "You're verified";
		title = `Finish joining ${latestCircleName}`;
		subtitle = [
			"You accepted this invitation. One tap to complete your membership",
			formatExpiry(latest.expiresAt),
		]
			.filter(Boolean)
			.join(" · ");
		ctaLabel = "Finish joining";
	} else if (isOnlyFinishJoining) {
		eyebrow = "You're verified";
		title = `Finish joining ${count} circles`;
		subtitle = "You accepted these invitations. Complete your memberships before they expire.";
		ctaLabel = "Finish joining";
	} else if (count === 1) {
		eyebrow = "New circle invitation";
		title = `You're invited to join ${latestCircleName}`;
		subtitle = [`${latest.inviterName} invited you`, formatExpiry(latest.expiresAt)]
			.filter(Boolean)
			.join(" · ");
		ctaLabel = "View invitation";
	} else {
		eyebrow = "Circle invitations";
		title = `You have ${count} circle invitations waiting`;
		subtitle =
			finishJoiningInvitations.length > 0 ?
				`${finishJoiningInvitations.length} ready to finish joining, ${newInvitations.length} new. Respond before they expire.`
			:	`Including ${latestCircleName} from ${latest.inviterName}. Respond before they expire.`;
		ctaLabel = "View invitations";
	}

	const openInvitations = () => navigate("/view-invitation");

	const handleDismiss = (event: React.MouseEvent) => {
		event.stopPropagation();
		const next = Array.from(
			new Set([...dismissedIds, ...visibleInvitations.map((inv) => inv._id)]),
		);
		setDismissedIds(next);
		try {
			sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(next));
		} catch {
			// Storage can be unavailable (private mode); dismissal still applies in memory.
		}
	};

	return (
		<Box
			role="region"
			aria-label="Pending circle invitations"
			onClick={openInvitations}
			sx={[
				{
					position: "relative",
					overflow: "hidden",
					cursor: "pointer",
					borderRadius: "20px",
					p: { xs: 2.25, sm: 3 },
					color: "#fff",
					background: `linear-gradient(120deg, ${buttonColors.blueRibbon} 0%, #1d4fd1 55%, ${buttonColors.mainSecondary} 130%)`,
					boxShadow: "0 18px 40px rgba(20, 58, 162, 0.28)",
					animation: `${slideIn} 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) both`,
					transition: "transform 0.25s ease, box-shadow 0.25s ease",
					"&:hover": {
						transform: "translateY(-3px)",
						boxShadow: "0 24px 50px rgba(20, 58, 162, 0.36)",
					},
					"&:hover .invite-cta-arrow": { transform: "translateX(4px)" },
					// Soft light sweep across the card
					"&::after": {
						content: '""',
						position: "absolute",
						top: 0,
						bottom: 0,
						left: 0,
						width: "35%",
						background:
							"linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)",
						animation: `${shimmer} 4.5s ease-in-out infinite`,
						pointerEvents: "none",
					},
					"@media (prefers-reduced-motion: reduce)": {
						animation: "none",
						"&::after": { animation: "none", display: "none" },
						"& *": { animation: "none !important" },
					},
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}>
			{/* Decorative circles */}
			<Box
				aria-hidden
				sx={{
					position: "absolute",
					right: -40,
					top: -60,
					width: 180,
					height: 180,
					borderRadius: "50%",
					background: "rgba(255,255,255,0.08)",
				}}
			/>
			<Box
				aria-hidden
				sx={{
					position: "absolute",
					right: 60,
					bottom: -70,
					width: 140,
					height: 140,
					borderRadius: "50%",
					background: "rgba(255,255,255,0.06)",
				}}
			/>

			<Tooltip title="Remind me later">
				<IconButton
					aria-label="Dismiss invitation reminder"
					size="small"
					onClick={handleDismiss}
					sx={{
						position: "absolute",
						top: 10,
						right: 10,
						zIndex: 1,
						color: "rgba(255,255,255,0.85)",
						"&:hover": { background: "rgba(255,255,255,0.15)" },
					}}>
					<CloseRoundedIcon fontSize="small" />
				</IconButton>
			</Tooltip>

			<Stack
				direction={{ xs: "column", sm: "row" }}
				alignItems={{ xs: "flex-start", sm: "center" }}
				spacing={{ xs: 2, sm: 2.5 }}
				sx={{ position: "relative", zIndex: 1, pr: { xs: 3, sm: 4 } }}>
				{/* Animated envelope with count badge */}
				<Box sx={{ position: "relative", flexShrink: 0 }}>
					<Box
						sx={{
							width: 56,
							height: 56,
							borderRadius: "16px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							background: "rgba(255,255,255,0.16)",
							backdropFilter: "blur(6px)",
							border: "1px solid rgba(255,255,255,0.25)",
						}}>
						<MarkEmailUnreadRoundedIcon
							sx={{
								fontSize: 30,
								animation: `${envelopeWiggle} 2.8s ease-in-out infinite`,
								transformOrigin: "50% 80%",
							}}
						/>
					</Box>
					<Box
						aria-label={`${count} pending`}
						sx={{
							position: "absolute",
							top: -7,
							right: -7,
							minWidth: 24,
							height: 24,
							px: 0.75,
							borderRadius: "12px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontFamily: FONT,
							fontSize: "0.75rem",
							fontWeight: 800,
							color: buttonColors.blueRibbon,
							background: buttonColors.mainSecondary,
							border: "2px solid #fff",
							animation: `${pulseRing} 2s ease-out infinite`,
						}}>
						{count > 9 ? "9+" : count}
					</Box>
				</Box>

				{/* Copy */}
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography
						sx={{
							fontFamily: FONT,
							fontSize: "0.72rem",
							fontWeight: 700,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
							color: "rgba(255,255,255,0.75)",
							mb: 0.5,
						}}>
						{eyebrow}
					</Typography>
					<Typography
						sx={{
							fontFamily: FONT,
							fontSize: { xs: "1.05rem", sm: "1.2rem" },
							fontWeight: 700,
							lineHeight: 1.3,
							wordBreak: "break-word",
						}}>
						{title}
					</Typography>
					<Typography
						sx={{
							fontFamily: FONT,
							fontSize: "0.88rem",
							color: "rgba(255,255,255,0.82)",
							mt: 0.5,
						}}>
						{subtitle}
					</Typography>
				</Box>

				{/* Circle avatars + CTA */}
				<Stack
					direction="row"
					alignItems="center"
					spacing={2}
					sx={{ flexShrink: 0, width: { xs: "100%", sm: "auto" } }}>
					<AvatarGroup
						max={3}
						sx={{
							display: { xs: "none", md: "flex" },
							"& .MuiAvatar-root": {
								width: 36,
								height: 36,
								fontSize: "0.85rem",
								border: "2px solid rgba(255,255,255,0.9)",
								background: "rgba(255,255,255,0.2)",
							},
						}}>
						{visibleInvitations.map((inv) => (
							<Avatar
								key={inv._id}
								alt={inv.circle?.circleName || "Circle"}
								src={inv.circle?.circleImage || undefined}>
								{(inv.circle?.circleName || "C").charAt(0).toUpperCase()}
							</Avatar>
						))}
					</AvatarGroup>

					<Button
						variant="contained"
						disableElevation
						onClick={(event) => {
							event.stopPropagation();
							openInvitations();
						}}
						endIcon={
							<ArrowForwardRoundedIcon
								className="invite-cta-arrow"
								sx={{ transition: "transform 0.25s ease" }}
							/>
						}
						sx={{
							width: { xs: "100%", sm: "auto" },
							px: 2.5,
							py: 1.1,
							borderRadius: "12px",
							textTransform: "none",
							fontFamily: FONT,
							fontWeight: 700,
							fontSize: "0.92rem",
							whiteSpace: "nowrap",
							color: buttonColors.blueRibbon,
							background: "#fff",
							"&:hover": { background: "#f1f5ff" },
						}}>
						{ctaLabel}
					</Button>
				</Stack>
			</Stack>
		</Box>
	);
};

export default PendingInvitesCta;
